import React from 'react'
import { ReferralJourney } from './referral-journey.js'
import { useCopyFeedback } from './use-copy-feedback.js'

export interface ReferralHeroLabels {
  /** mono eyebrow, e.g. "Referral". */
  eyebrow: string
  /** Serif headline, e.g. "邀請越多，賺越多。". */
  headline: string
  /** Sub-line. The per-referral amount lives HERE (copy), never hard-coded into
   *  the structure, so the price can change in one place. */
  sub: string
  /** Three journey-step labels (share → friend subscribes → both rewarded). */
  journeyShare: string
  journeyFriend: string
  journeyReward: string
  /** Copy-link button idle + done labels. */
  copy: string
  copied: string
  /** "How it works & FAQ →" link label. */
  more: string
}

/** How long the button reads "copied" after a copy that worked. */
const COPIED_MS = 1200

export interface ReferralHeroProps {
  labels: ReferralHeroLabels
  /** The user's share link (already built from the live origin + code). */
  shareUrl: string
  /** Public how-it-works / FAQ page URL (the single source of truth). */
  moreHref: string
  /** Open the "more" link in a new tab (web marketing page). */
  moreTarget?: '_blank'
  /** Called after a copy that worked (analytics / tests). The component
   *  writes to the clipboard itself. */
  onCopy?: (url: string) => void
}

/**
 * The reusable black ink-band referral invite section: eyebrow + serif
 * headline + sub, the animated 3-node "journey" (share → friend subscribes →
 * both rewarded), a copy-the-link row, and a link out to the public
 * how-it-works/FAQ page. Rendered by the web in-app referral page through
 * {@link ReferralView}.
 *
 * Visuals are driven entirely by `.ref` / `.journey` / `.copyrow` /
 * `.btn-onink` in kiao-app.css; the journey path animation is declarative
 * (CSS @keyframes + SVG <animateMotion>), so no JS timers run here.
 */
export function ReferralHero({
  labels,
  shareUrl,
  moreHref,
  moreTarget,
  onCopy,
}: ReferralHeroProps) {
  const { copied, copy } = useCopyFeedback(COPIED_MS)

  const handleCopy = () =>
    void copy(async () => {
      // Clipboard can be blocked (permissions / insecure context) or missing;
      // the link stays visible in the readonly input either way.
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        throw new Error('Clipboard is not available')
      }
      await navigator.clipboard.writeText(shareUrl)
    }).then((done) => {
      if (done) onCopy?.(shareUrl)
    })

  return (
    <section className="ref" data-testid="referral-hero">
      <p className="eyebrow">{labels.eyebrow}</p>
      <h2>{labels.headline}</h2>
      <p className="sub">{labels.sub}</p>

      <ReferralJourney
        labels={[labels.journeyShare, labels.journeyFriend, labels.journeyReward]}
      />

      <div className="copyrow">
        <input className="input" readOnly value={shareUrl} data-testid="referral-share-link" />
        <button type="button" className="btn btn-onink" onClick={handleCopy} data-testid="referral-copy">
          {copied ? labels.copied : labels.copy}
        </button>
      </div>

      <a
        className="ref-more"
        href={moreHref}
        target={moreTarget}
        rel={moreTarget ? 'noopener noreferrer' : undefined}
        data-testid="referral-more-link"
      >
        {labels.more}
      </a>
    </section>
  )
}
